import { ahuBoardApi } from './ahuBoardApi';
import { getCookie, getKsdmConfig } from 'helpers/index';

/**
 * Phase 4b — the `/sizer/` data layer.
 *
 * Ports the Backbone collection/model graph of
 * `django/sizer/static/sizer/mvc/collection/all.js` +
 * `django/sizer/static/sizer/mvc/model/all.js`, i.e. every GET the page fires
 * on load (phase4_sizer_discovery §3, OBSERVED order in `p4/network.json`).
 *
 * Injected into `ahuBoardApi` (one reducerPath, no `store.ts` wiring) like the
 * 15 other `injectEndpoints` call sites; endpoint names carry a `Sizer` prefix
 * so they cannot collide with the AHU-board selectors that hit some of the
 * same URLs (e.g. `filter_sizes/`, `tube_dias/`).
 *
 * ★ PARITY NOTES
 *
 * 1. Backbone `Collection.fetch()` on an endpoint that returns a bare OBJECT
 *    wraps it in a one-element array (`Backbone.Collection.set`: `singular =
 *    !_.isArray(models)`), which is why the legacy view reads `.toJSON()[0]`
 *    for coil settings / dimensions / deviations (view/main.js:254, 264-265).
 *    Those endpoints DO return objects on dev3 — verified live 2026-07-27.
 *    `transformResponse` unwraps to the single row so callers get the same
 *    thing `toJSON()[0]` gave, array-or-object tolerant.
 *
 * 2. Every CMS URL except the casing insulations is HARD-CODED to `cms/1/`
 *    in the legacy collections (collection/all.js:15, 20, 56, 60, 64, 68, 98)
 *    — literally the string "1", NOT the user's mfg. Only the insulations go
 *    through `fn.uni_tools.mapUrlToCMS` (universal_fn_tools.js:128-131), which
 *    substitutes the logged-in user's mfg id. Reproduced verbatim: a quirk,
 *    not a bug to fix (on dev3 both are 1, so it is unobservable there).
 *
 * 3. `masterSizerTube` / `MasterAHUCoilCasingFlangeWidth` / `MasterSizerCoilFH`
 *    are pinned to mfg 21 (Coilmaster) + coil type 1 (fluid) —
 *    collection/all.js:6-7 module constants, not derived from anything.
 *
 * 4. NOT PORTED — `GET /api/v1.0/cms/1/v1.0.0/ahu/products/`
 *    (`CMSProductCollection`, collection/all.js:19-21, fetched at
 *    view/main.js:115 and handed to the template at :255). Discovery §10
 *    classes it DEAD: no template ever references `cmsProductCollection`, so
 *    the request is pure waste. The payload's `product_name` is the hardcoded
 *    literal `1` (view/main.js:522), not derived from it.
 *
 * 5. `keepUnusedDataFor` — CMS/master catalogs are session-stable, so they get
 *    the `ahuBoardApi.ts:52` forever-cache. This makes a return trip to a
 *    previously-selected Product Class serve from cache where legacy re-fetched
 *    (`getDataDependsOnConstructionClass`, main.js:408-412). Behaviour-
 *    preserving: the responses are pure functions of the construction class.
 */

/** Session-long cache (mirrors `ahuBoardApi.ts:52`). */
const KEEP_FOREVER_SECONDS = 24 * 60 * 60;

/** Coilmaster + fluid — collection/all.js:6-7. */
export const SIZER_COMP_MFG_ID = 21;
export const SIZER_COIL_TYPE_ID = 1;

/** The literal `1` the legacy sizer collections bake into their CMS URLs. */
const CMS_LITERAL = 1;

/** `fn.uni_tools.mapUrlToCMS` — the CMS id is the LOGGED-IN user's mfg id. */
function mapUrlToCmsId(): number {
  return getKsdmConfig()?.mfg?.id ?? 1;
}

// ---------------------------------------------------------------------------
// row shapes (all verified live against dev3 mfg 1 on 2026-07-27)
// ---------------------------------------------------------------------------

/**
 * `/cms/1/v1.0.0/ahu/construction_class/` — the Product Class options.
 * The label is `{{get_one label construction_class}}` (settings.tmpl:15), i.e.
 * `label || construction_class || ''` (helpers.js:94-96).
 */
export interface ISizerConstructionClass {
  id: number;
  mfg_id?: number;
  construction_class_id?: number;
  label?: string | null;
  construction_class?: string | null;
  construction?: string | null;
  description?: string | null;
  [k: string]: unknown;
}

/** `/master/v1.0.0/ahu/filter_sizes/` — 10 rows on dev3. */
export interface ISizerFilterSize {
  id: number;
  name: string;
  /** the token the matrix payload joins with commas, e.g. `w12_h24`. */
  value: string;
  width: number;
  height: number;
  order_id: number;
}

/** `/cms/1/v1.0.0/ahu/coils/mfg/settings/{coolings,heatings}/` (bare object). */
export interface ISizerCoilMfgSettings {
  id: number;
  mfg_id: number;
  min_face_velocity: number;
  max_face_velocity: number;
  min_qty_stacked: number;
  max_qty_stacked: number;
  [k: string]: unknown;
}

/** `/cms/1/v1.0.0/ahu/dynamic_sizer/settings/` (bare object) — 4d's window. */
export interface ISizerDeviation {
  mfg_id: number;
  product_id: number | null;
  deviation_width_min: number;
  deviation_width_max: number;
  deviation_height_min: number;
  deviation_height_max: number;
}

/** `/cms/1/v1.0.0/ahu/dimensions/?construction_class_id=` (bare object). */
export interface ISizerCabinetDimensions {
  id: number;
  mfg_id: number;
  construction_class_id: number;
  tunnel_cabinet_width_min: number;
  /** 4d: `scrollToTheMostOptimal`'s guard + clamp (matrix.js:143-146, :163). */
  tunnel_cabinet_width_max: number;
  tunnel_cabinet_height_min: number;
  tunnel_cabinet_height_max: number;
  tunnel_cabinet_width_increment: number;
  tunnel_cabinet_height_increment: number;
  /** the grid axes — 277 entries for construction class 1, 139 for class 5. */
  width_values: number[];
  height_values: number[];
  width_value_names?: string[];
  height_value_names?: string[];
  [k: string]: unknown;
}

/**
 * The `{id, item:{...}}` master-row envelope shared by tube diameters, casing
 * flange widths and coil fin heights. `id` is the mfg/type JOIN row id; `item`
 * is the catalogue row the template actually renders.
 *
 * ⚠ The settings selects key off `item.id`, NOT the envelope `id`
 * (settings.tmpl:79 `value="{{this.item.id}}"`), which is why the FH lookup URL
 * carries `tube_dias/4/` (item id of 5/8") and not `tube_dias/3/` (row id) —
 * OBSERVED in `p4/network.json`.
 */
export interface ISizerMasterRow {
  id: number;
  item: {
    id: number;
    /** tube diameters render `name` ("5/8"); FHs render `name` as a NUMBER. */
    name?: string | number;
    value: number;
  };
}

/** `/cms/1/v1.0.0/ahu/dynamic_sizer/optimal_sizes/` — 4d's interpolation graph. */
export interface ISizerOptimalSize {
  id: number;
  mfg_id: number;
  product_id: number | null;
  airflow: number;
  optimal_width: number;
  optimal_height: number;
}

/** `/cms/{mfg}/v1.0.0/ahu/casing/insulations/?construction_class_id=`. */
export interface ISizerCasingInsulation {
  id: number;
  model: string;
  thickness: number;
  thickness_name: string;
  label?: string | null;
  name?: string | null;
  [k: string]: unknown;
}

// ---------------------------------------------------------------------------
// the matrix RPC contract — TYPES ONLY (the endpoint itself lands with 4c)
// ---------------------------------------------------------------------------

/**
 * ★ 4c SEAM. The exact body `updateMatrixData` builds (view/main.js:520-562),
 * in source key order — `JSON.stringify` preserves insertion order, and the
 * verify step diffs this body key-for-key against the OBSERVED capture in
 * discovery §3. `MatrixView` then adds `page_coordinates` (matrix.js:558) and,
 * after a Size click, `optimal` (matrix.js:110-111) — both declared optional
 * here so 4c can spread onto this object.
 *
 * Quirks baked in on purpose (do NOT "fix" — discovery §3 / risk 7):
 *  - `product_name` is the literal 1 (:522).
 *  - the six `*_fan_max_*` keys are always 8: no template renders
 *    `input[name=plenum_fan_wide]` &c, so `+undefined || 8` (:524-529).
 *  - `cc_tube`/`hc_tube` carry the option TEXT (the diameter 0.625), not the
 *    option value/id (:532-535, :542-545).
 *  - `*_casing_flange_id` is ALWAYS NaN → serialises to `null`: the lookup
 *    uses `option[value="1.50"]` (`toFixed(2)`) but the option's value
 *    attribute is `1.5` (:537, :547).
 *  - `filter_frame_loading_id` is the literal 1 (:553) — its select lives in
 *    the hidden duplicate block and its collection is never fetched.
 */
export interface ISizerMatrixRequest {
  airflow: number;
  product_name: number;
  thickness: number | null;
  plenum_fan_max_wide: number;
  plenum_fan_max_high: number;
  cube_fan_max_wide: number;
  cube_fan_max_high: number;
  plug_fan_max_wide: number;
  plug_fan_max_high: number;
  cc_face_vel_min: number;
  cc_face_vel_max: number;
  cc_tube: number;
  cc_top_and_btm_casing_flange: number;
  /** always NaN in practice → `null` on the wire. */
  cc_top_and_btm_casing_flange_id: number | null;
  cc_max_fh: number;
  cc_qty_stacked: number;
  hc_face_vel_min: number;
  hc_face_vel_max: number;
  hc_tube: number;
  hc_top_and_btm_casing_flange: number;
  hc_top_and_btm_casing_flange_id: number | null;
  hc_max_fh: number;
  hc_qty_stacked: number;
  filter_face_vel_min: number;
  filter_face_vel_max: number;
  max_different_sizes: number;
  filter_frame_loading_id: number;
  construction_class_id: number;
  casing_insulation_id: number;
  /** comma-joined filter-size `value` tokens, e.g. `w12_h24,w24_h12`. */
  allowable_filter_sizes: string;
  /** added by MatrixView.load_page (matrix.js:558) — 4c. */
  page_coordinates?: [number, number];
  /** added by MatrixView.updateOptimals (matrix.js:110) — 4d. */
  optimal?: [number, number, number];
}

/** One component readout inside a matrix cell (api/rest_rpc/viewset.py:249-256). */
export interface ISizerCellComponent {
  fh?: string | null;
  fl?: string | null;
  qty?: number | null;
  face_velocity?: number | string | null;
  face_area?: string | null;
  size_qty_id?: number | null;
  size_qty?: string | null;
  /** `in_face_velocity_range` | `out_of_face_velocity_range` — used directly
   * as a CSS class in block.tmpl:17,29,40. */
  status?: string;
}

/** One matrix cell. */
export interface ISizerCell {
  current_width: string;
  filter: ISizerCellComponent;
  cooling_coil: ISizerCellComponent;
  heating_coil: ISizerCellComponent;
  fan: Record<string, { wide: number; high: number; size: number; type: string; index: number }>;
}

/** One matrix row (10 cells per page). */
export interface ISizerMatrixRow {
  current_height: string;
  elements: ISizerCell[];
}

/** `POST /api/v1.0/rpc/sizer_matrix_data/` — `width`/`height` only when
 * `optimal` was sent (viewset.py:475-487). */
export interface ISizerMatrixResponse {
  width?: number;
  height?: number;
  results: ISizerMatrixRow[];
}

// ---------------------------------------------------------------------------
// ★ 4c — the matrix RPC itself. NOT an RTK Query endpoint, on purpose.
// ---------------------------------------------------------------------------

const SIZER_MATRIX_URL = '/api/v1.0/rpc/sizer_matrix_data/';

/**
 * WHY PLAIN `fetch` AND NOT RTK QUERY (deliberate, discussed in discovery §5
 * "Performance" which suggested caching on `(settings, page_x, page_y)`):
 *
 *  - The matrix ALREADY has a cache — `MatrixView.pages` (matrix.js:40), wiped
 *    wholesale by `update_data` (`:96`). Legacy's POST count is a direct
 *    function of that one cache, and the verify step diffs the POST stream
 *    against the live capture. A second, longer-lived RTK cache in front of it
 *    would silently swallow re-POSTs (16 on a Size click become ~5) and make
 *    that diff meaningless.
 *  - `page_coordinates` leaks into `updateOptimals`' body (matrix.js:558/:111)
 *    and `optimal` leaks into every later `load_page` body — so the cache key
 *    would have to include quirks that exist only to be reproduced.
 *  - RTK Query is built for STATE; these responses are consumed imperatively by
 *    the windowing engine and never rendered from the store.
 *
 * `postSizerMatrixData` is therefore a byte-for-byte port of
 * `fn.tools.data_exchanger` (sizer/mvc/fn/tools.js:20-30): POST, JSON body,
 * `Content-type`/`X-CSRFToken`/`X-Requested-With`, and — exactly like legacy —
 * NO `response.ok` check, so a 400 resolves with whatever the body parses to
 * and the caller behaves as legacy does.
 */
export async function postSizerMatrixData(
  body: ISizerMatrixRequest
): Promise<ISizerMatrixResponse> {
  const response = await fetch(SIZER_MATRIX_URL, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-type': 'application/json',
      'X-CSRFToken': getCookie('csrftoken') ?? '',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify(body),
  });
  return response.json();
}

/**
 * The `updateOptimals` call site (matrix.js:112-131) instead goes through
 * Backbone `blSizerMatrixData.save(...)`, whose `success` callback runs ONLY on
 * a 2xx — so a failed optimal request leaves `self.width`/`self.height` and the
 * deviation window untouched and skips `scrollToTheMostOptimal` entirely (there
 * is no `error` callback). `null` here is that "success never ran" signal.
 */
export async function saveSizerMatrixData(
  body: ISizerMatrixRequest
): Promise<ISizerMatrixResponse | null> {
  const response = await fetch(SIZER_MATRIX_URL, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-type': 'application/json',
      'X-CSRFToken': getCookie('csrftoken') ?? '',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) return null;
  return response.json();
}

// ---------------------------------------------------------------------------

/** Backbone `toJSON()[0]` equivalent for the bare-object endpoints (note 1). */
function firstRow<T>(response: T | T[]): T | null {
  if (Array.isArray(response)) return response.length ? response[0] : null;
  return response ?? null;
}

export const sizerApi = ahuBoardApi.injectEndpoints({
  endpoints: (builder) => ({
    // ---- §3 #1 — fn['sizer.pages.size'] (fn/map.js:201-203) → [10,10] ----
    getSizerPageSize: builder.query<[number, number], void>({
      query: () => '/api/v1.0/rpc/sizer_matrix_data/page_size/',
      keepUnusedDataFor: KEEP_FOREVER_SECONDS,
    }),

    // ---- §3 #5 — models.SizerPagesCount (model/all.js:58-66) → [28,28] ----
    // 400s without the param; construction class 5 answers [14,28] (its width
    // increment is 2), which is why 4c must re-derive geometry, never assume.
    getSizerPagesCount: builder.query<[number, number], number | string>({
      query: (constructionClassId) =>
        `/api/v1.0/rpc/sizer_matrix_data/pages_count/?construction_class_id=${constructionClassId}`,
      keepUnusedDataFor: KEEP_FOREVER_SECONDS,
    }),

    // ---- §3 #4 — collections.ConstructionClass (collection/all.js:97-99) ----
    getSizerConstructionClasses: builder.query<ISizerConstructionClass[], void>({
      query: () => `/api/v1.0/cms/${CMS_LITERAL}/v1.0.0/ahu/construction_class/`,
      keepUnusedDataFor: KEEP_FOREVER_SECONDS,
    }),

    // ---- §3 #6 — FilterAvaiableSizes (collection/all.js:93-95) [sic] ----
    getSizerFilterSizes: builder.query<ISizerFilterSize[], void>({
      query: () => '/api/v1.0/master/v1.0.0/ahu/filter_sizes/',
      keepUnusedDataFor: KEEP_FOREVER_SECONDS,
    }),

    // ---- §3 #7 — CoolingCoilSettings (collection/all.js:55-57) ----
    getSizerCoolingCoilSettings: builder.query<ISizerCoilMfgSettings | null, void>({
      query: () => `/api/v1.0/cms/${CMS_LITERAL}/v1.0.0/ahu/coils/mfg/settings/coolings/`,
      transformResponse: firstRow<ISizerCoilMfgSettings>,
      keepUnusedDataFor: KEEP_FOREVER_SECONDS,
    }),

    // ---- §3 #8 — HeatingCoilSettings (collection/all.js:59-61) ----
    getSizerHeatingCoilSettings: builder.query<ISizerCoilMfgSettings | null, void>({
      query: () => `/api/v1.0/cms/${CMS_LITERAL}/v1.0.0/ahu/coils/mfg/settings/heatings/`,
      transformResponse: firstRow<ISizerCoilMfgSettings>,
      keepUnusedDataFor: KEEP_FOREVER_SECONDS,
    }),

    // ---- §3 #9 — Deviation (collection/all.js:67-69) — consumed by 4d ----
    getSizerDeviation: builder.query<ISizerDeviation | null, void>({
      query: () => `/api/v1.0/cms/${CMS_LITERAL}/v1.0.0/ahu/dynamic_sizer/settings/`,
      transformResponse: firstRow<ISizerDeviation>,
      keepUnusedDataFor: KEEP_FOREVER_SECONDS,
    }),

    // ---- §3 #10 — CabinetDimensions (collection/all.js:9-17) — grid geometry.
    // Re-fetched on every Product Class change (main.js:409).
    getSizerCabinetDimensions: builder.query<ISizerCabinetDimensions | null, number | string>({
      query: (constructionClassId) =>
        `/api/v1.0/cms/${CMS_LITERAL}/v1.0.0/ahu/dimensions/?construction_class_id=${constructionClassId}`,
      transformResponse: firstRow<ISizerCabinetDimensions>,
      keepUnusedDataFor: KEEP_FOREVER_SECONDS,
    }),

    // ---- §3 #11 — MasterSizerTube (collection/all.js:51-53) ----
    getSizerTubeDias: builder.query<ISizerMasterRow[], void>({
      query: () =>
        `/api/v1.0/master/v1.0.0/ahu/coils/tube_dias/mfgs/${SIZER_COMP_MFG_ID}/types/${SIZER_COIL_TYPE_ID}/`,
      keepUnusedDataFor: KEEP_FOREVER_SECONDS,
    }),

    // ---- §3 #13 — MasterAHUCoilCasingFlangeWidth (collection/all.js:31-33) ----
    getSizerCasingFlangeWidths: builder.query<ISizerMasterRow[], void>({
      query: () =>
        `/api/v1.0/master/v1.0.0/ahu/coils/casing_flange_widths/mfgs/${SIZER_COMP_MFG_ID}/types/${SIZER_COIL_TYPE_ID}/`,
      keepUnusedDataFor: KEEP_FOREVER_SECONDS,
    }),

    // ---- §3 #14 — OptimalWidthAndHeight (collection/all.js:63-65) — 4d ----
    getSizerOptimalSizes: builder.query<ISizerOptimalSize[], void>({
      query: () => `/api/v1.0/cms/${CMS_LITERAL}/v1.0.0/ahu/dynamic_sizer/optimal_sizes/`,
      keepUnusedDataFor: KEEP_FOREVER_SECONDS,
    }),

    // ---- §3 #15 — CMSCasingInsulationCollection (collection/all.js:83-91) ----
    // The ONLY sizer URL that resolves the CMS id from the user's mfg (note 2).
    getSizerCasingInsulations: builder.query<ISizerCasingInsulation[], number | string>({
      query: (constructionClassId) =>
        `/api/v1.0/cms/${mapUrlToCmsId()}/v1.0.0/ahu/casing/insulations/?construction_class_id=${constructionClassId}`,
      keepUnusedDataFor: KEEP_FOREVER_SECONDS,
    }),

    // ---- §3 #16 — MasterSizerCoilFH (collection/all.js:39-49) ----
    // Keyed by the tube select's value = the tube diameter's ITEM id. Legacy
    // memoises the result per tube id in `self.state` (main.js:187-188) so the
    // cooling+heating pair only costs one GET; RTK Query's cache is that memo.
    getSizerCoilFhs: builder.query<ISizerMasterRow[], number | string>({
      query: (tubeDiaItemId) =>
        `/api/v1.0/master/v1.0.0/ahu/coils/fhs/mfgs/${SIZER_COMP_MFG_ID}/types/${SIZER_COIL_TYPE_ID}/tube_dias/${tubeDiaItemId}/`,
      keepUnusedDataFor: KEEP_FOREVER_SECONDS,
    }),
  }),
});

export const {
  useGetSizerPageSizeQuery,
  useGetSizerPagesCountQuery,
  useGetSizerConstructionClassesQuery,
  useGetSizerFilterSizesQuery,
  useGetSizerCoolingCoilSettingsQuery,
  useGetSizerHeatingCoilSettingsQuery,
  useGetSizerDeviationQuery,
  useGetSizerCabinetDimensionsQuery,
  useGetSizerTubeDiasQuery,
  useGetSizerCasingFlangeWidthsQuery,
  useGetSizerOptimalSizesQuery,
  useGetSizerCasingInsulationsQuery,
  useGetSizerCoilFhsQuery,
} = sizerApi;
